import { Injectable } from '@angular/core';
import { APIErrorModel, BaseResponse } from '../../shared/models/base-response.model';
import { ActivityRequestModel, ActivityResponseModel, AdsActivityRequestModel, CatalogueAdsResponseModel, ParamsToGetAdsList, PavilionListResponseModel, ProductDetailsResponseModel, ProductListResponseModel } from './digital-catalogue.model';
import { CompanyDetailsModel, CompanyListModel, ParamsToGetCompanyList, ParamsToGetProductsList, PavilionGroupModel } from './digital-catalogue.model';
import { firstValueFrom, Subscription } from 'rxjs';
import { HttpResult } from '../../core/services/base-http.service';
import { DigitalCatalogueApiService } from './digital-catalogue-api.service';
import { StorageService } from '../../core/services/storage.service';
import { NavigationEnd, Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class DigitalCatalogueBllService {
  private previousUrl: string | null = null;
  private currentUrl: string | null = null;
  private routerSubscription!: Subscription; // store the subscription

  constructor(
    private api: DigitalCatalogueApiService,
    private storageService: StorageService,
    private router: Router
  ) { 
    this.currentUrl = this.router.url;
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Fetch the list of pavilions
   * @returns pavilion list
   */
  async fetchPavilionList(): Promise<BaseResponse<PavilionListResponseModel>> {
    const result: HttpResult<BaseResponse<PavilionListResponseModel>> =
      await firstValueFrom(this.api.getPavilionListApi());

    const { status, body } = result;
    if (status === 200) {
      return body;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }
  /**
   * Fetch the list of companies
   * @returns company list
   */
  async fetchCompaniesList(params?: ParamsToGetCompanyList, fromWishlist?: boolean): Promise<BaseResponse<CompanyListModel>> {
    const result: HttpResult<BaseResponse<CompanyListModel>> =
      await firstValueFrom(this.api.getCompaniesList(params, fromWishlist));


    const { status, body } = result;
    if (status === 200) {
      return body;
    } else {
      throw new Error('Failed to fetch companies list');
    }
  }


  /**
   * Save selected pavilion details to storage to be used across components
   * @param pavilionName Name of the pavilion
   * @param pavilionImgUrl Image URL of the pavilion
   */
  saveSelectedPavilionDetails(pavilion_code: string, pavilionName: string, pavilionImgUrl: string) {
    this.storageService.saveSelectedPavilionCode(pavilion_code);
    this.storageService.saveSelectedPavilionName(pavilionName);
    this.storageService.saveSelectedPavilionImgUrl(pavilionImgUrl);
 }

 
  async fetchCompanyDetails(companyId: string): Promise<BaseResponse<CompanyDetailsModel>> {

    const result: HttpResult<BaseResponse<CompanyDetailsModel>> =
      await firstValueFrom(this.api.getCompanyDetails(companyId));

    const { status, body } = result;
    if (status === 200) {
      return body;
    } else {
      throw new Error('Failed to fetch companies list');
    }
  }

  getSelectedPavilionDetails() {
    const pavilionCode = this.storageService.getSelectedPavilionCode();
    const pavilionName = this.storageService.getSelectedPavilionName();
    const pavilionImgUrl = this.storageService.getSelectedPavilionImgUrl();
    return { pavilionCode, pavilionName, pavilionImgUrl };
  }

  async fetchProductList(
    params?: ParamsToGetProductsList,
    fromWishlist?: boolean
  ): Promise<BaseResponse<ProductListResponseModel>> {
    const result: HttpResult<BaseResponse<ProductListResponseModel>> =
      await firstValueFrom(this.api.getProductListApi(params, fromWishlist));

    const { status, body } = result;
    if (status === 200) {
      return body;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  async fetchProductDetails(productId: number | string): Promise<BaseResponse<ProductDetailsResponseModel>> {
    const result: HttpResult<BaseResponse<ProductDetailsResponseModel>> =
      await firstValueFrom(this.api.getProductDetails(productId));

    const { status, body } = result;
    if (status === 200) {
      return body;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  async submitUserActivity(payload: ActivityRequestModel) {
    const result: HttpResult<BaseResponse<ActivityResponseModel>> =
      await firstValueFrom(this.api.saveUserActivityApi(payload));

    const { status, body } = result;

    if (status === 200) {
      return body;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  public getPreviousUrl(): string | null {
    return this.previousUrl;
  }

   getShortListMessage(isLiked: boolean): string {
    return isLiked ? 'Added to Shortlist' : 'Removed from Shortlist';
  }

  getLinearGradient(maxColor?: string, defaultColor: string = '#008940'): string {
    const color = maxColor || defaultColor;
    return `linear-gradient(90deg, white -10%, ${color} 90%)`;
  }
  
  saveSelectedCategoryDetails(categoryImgUrl: string, categoryName: string) {
    this.storageService.saveSelectedCategoryImgUrl(categoryImgUrl);
    this.storageService.saveSelectedCategoryName(categoryName);
  }

  getSelectedCategoryDetails() {
    const categoryName = this.storageService.getSelectedCategoryName();
    const categoryImgUrl = this.storageService.getSelectedCategoryImgUrl();
    return { categoryName, categoryImgUrl };
  }

  openLayout(){
     window.open('https://kisan-common.s3.ap-south-1.amazonaws.com/event/kisan25/kisan2025_layout.pdf', '_self');
  }

  /**
   * Fetch ads for digital catalogue
   * @returns Promise resolving to BaseResponse containing CatalogueAdsResponseModel array
   */
  async fetchAds(params?: ParamsToGetAdsList): Promise<BaseResponse<CatalogueAdsResponseModel[]>> {
    const result: HttpResult<BaseResponse<CatalogueAdsResponseModel[]>> =
      await firstValueFrom(this.api.getAds(params));

    const { status, body } = result;
    if (status === 200) {
      return body;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  /**
   * Submit ads activity
   * @param payload AdsActivityRequestModel payload
   * @returns Promise resolving to BaseResponse containing ActivityResponseModel
   */
  async submitAdsActivity(payload: AdsActivityRequestModel) {
    const result: HttpResult<BaseResponse<ActivityResponseModel>> =
      await firstValueFrom(this.api.saveAdsActivityApi(payload));

    const { status, body } = result;

    if (status === 200) {
      return body;
    } else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

}
